// utils/portalNav.js
//
// THE list of portal navigation pages, with their role gating — extracted from
// Sidebar.jsx (2026-08-15) so the dashboard's shortcut picker can offer the
// same pages the sidebar shows. One builder, two consumers: if the gating were
// duplicated instead, the picker would inevitably drift from the sidebar and
// offer someone a page their menu doesn't have (or hide one it does).
//
// These are PURE functions of ({ currentOrg, user, isAdmin, isSuperAdmin }) —
// no hooks — so the picker can call them outside the sidebar's render and
// tests can drive every branch directly.
//
// The item shape matches what the sidebar always rendered:
//   { href, label, icon (react-icons component), badge?, requestsBadge? } | { type: 'divider' }
//
// `requestsBadge` marks the Org Admin entry: the sidebar puts the red count of
// unanswered membership requests on it (hooks/usePendingMembershipRequests),
// which reads 0 for non-admins — so the group menu's all-members Admin item
// can carry the flag too.
import {
    FaHome, FaKey, FaWallet, FaInfoCircle, FaBriefcase, FaBuilding, FaUsers, FaRobot,
    FaUsersCog, FaWpforms, FaImages, FaCalendarAlt, FaCog, FaChartBar,
    FaUserShield, FaBroadcastTower, FaBell, FaUserClock, FaClipboardCheck,
} from 'react-icons/fa';
import { houseNavItemsFor } from '@/utils/houseNav';
import { isAccountType, isOrgAccountType } from '@/utils/builtInApplets';
import { isIntelUser } from '@/utils/intel';
import { isNotificationsEnabled } from '@/utils/notificationsGate';
import { isAiAppletsEnabled } from '@/utils/aiAppletsGate';

/**
 * The App Access folder (App Access retrofit, 2026-08-25 legacy release):
 * an expandable group replacing the old single "App Access Roster" item.
 * Mirrors legacy NavMenu/GroupMenu: Org App Access routes by org flavor;
 * Special App Access and the Approval Queue are org-admin pages. Items
 * marked `countBadge` show the live pending-approvals count (red pill),
 * which the sidebar reads from usePendingApprovals — this registry stays
 * pure.
 */
function appAccessFolder(orgHref, includeAdminPages) {
    // Child `color` renders on a white tile in the sidebar (the navImgBorder
    // idiom); values are legacy NavMenu.razor's verbatim.
    const children = [{ href: orgHref, label: 'Org App Access', icon: FaUsers, color: '#1565C0' }];
    if (includeAdminPages) {
        children.push({ href: '/specialappaccess', label: 'Special App Access', icon: FaUserClock, color: '#f0ad4e' });
        children.push({ href: '/approvalqueue', label: 'Approval Queue', icon: FaClipboardCheck, color: '#1C830E', countBadge: true });
    }
    return {
        type: 'folder', key: 'app-access-nav', label: 'App Access',
        icon: FaUsers, countBadge: true, children,
    };
}

/**
 * Main navigation for the current account, exactly as the sidebar shows it.
 */
export function buildPortalNavItems({ currentOrg, user, isAdmin, isSuperAdmin }) {
    const items = [];

    // SHARED TRAINING CAL: NO NAVIGATION AT ALL.
    // Legacy TrainingCalShared.razor declares `@layout TrainingCalLayout`
    // (Shared/TrainingCalLayout.razor): the FSE logo and a Logout button, no
    // menu — not even Home or My Account. The port used to hand these logins
    // Home + My Account + Training Calendar (audit 12h, OUTSTANDING §C57).
    // ClientLayout drops the sidebar for this account type; this empty list
    // keeps the dashboard shortcut picker in step with it.
    if (isAccountType(currentOrg, 'SharedTrainingCal')) return items;

    items.push({ href: '/', label: 'Home', icon: FaHome });
    items.push({ href: '/myaccount', label: 'My Account', icon: FaKey });

    if (!currentOrg) return items;

    // SHARED HOUSE NAVIGATION
    if (isAccountType(currentOrg, 'SharedHouse')) {
        // NO "Back" here, deliberately. A SharedHouse login belongs to exactly
        // one house, and `/firehouseadmin` — the house list this used to point
        // at — immediately does `router.replace('/houseadmin')` for these
        // accounts (firehouseadmin/page.js). So the link was a round trip to
        // where the user already was: it appeared to offer somewhere to go and
        // then returned them, unchanged, to the same screen.
        // Owner request 2026-08-08: "there is nowhere to go."
        // A full portal user who drills into a house from the house list DOES
        // still get a Back — that one is in PortalHouseSidebar, and for them
        // the house list is a real destination.
        items.push({ href: '/houseadmin', label: 'House Admin', icon: FaBriefcase });
        items.push({ type: 'divider' });
        items.push(...houseNavItemsFor(currentOrg.orgId || currentOrg.OrgId));

        return items;
    }

    // HOUSE ONLY NAVIGATION
    if (isOrgAccountType(currentOrg, 'HouseOnly')) {
        if (isAdmin) {
            items[0] = { href: '/househome', label: 'Home', icon: FaHome };
        }

        items.push({ href: '/houseadmin', label: 'House Admin', icon: FaBriefcase });
        items.push({ href: '/housesubscriptions', label: 'Subscriptions', icon: FaWallet });
        items.push({ href: '/helpcenterhouse', label: 'Help Center', icon: FaInfoCircle });
        items.push({ type: 'divider' });
        items.push(...houseNavItemsFor(currentOrg.orgId || currentOrg.OrgId));

        return items;
    }

    // GROUP APPLET NAVIGATION
    // Mirrors legacy Shared/GroupMenu.razor: a reduced menu with no
    // Staffing, no super-admin tools, and a single App Access Roster.
    // Home stays at '/' — legacy /grouphome is the older duplicate of Index.
    if (isOrgAccountType(currentOrg, 'GroupApplet')) {
        items.push({ href: '/admin', label: 'Admin', icon: FaBuilding, requestsBadge: true });
        // Per legacy GroupMenu: the folder (and Org App Access inside it)
        // stays visible to ALL members, as the roster item always was;
        // Special + Queue appear for org admins only.
        items.push(appAccessFolder('/namedaccess', isAdmin));
        if (isAdmin) {
            items.push({ href: '/portalusers', label: 'Portal Users', icon: FaUsersCog });
            // Notifications (08/2026) — directly under Portal Users, org-admin
            // only, mirroring legacy GroupMenu.razor. Visibility is decided by
            // notificationsGate.js, the single org-specific seam in the whole
            // feature: to enable for every group applet org, open that gate;
            // nothing here needs editing.
            if (isNotificationsEnabled(currentOrg.orgId || currentOrg.OrgId)) {
                items.push({ href: '/notifications', label: 'Notifications', icon: FaBell });
            }
            // Group orgs: legacy GroupMenu.razor L172 wraps Forms in
            // `Roles="orgadmin"` ALONE — the `forms` applet role that opens
            // the standard-org entry (below) does not apply here.
            items.push({ href: '/formslibrary', label: 'Forms Library', icon: FaWpforms, badge: 'Beta' });
        }
        items.push({ href: '/subscriptions', label: 'Subscriptions', icon: FaWallet });
        if (isAdmin && isAiAppletsEnabled(currentOrg.orgId || currentOrg.OrgId, user?.id)) {
            items.push({ href: '/aidashboard', label: 'AI Dashboard', icon: FaRobot, badge: 'Beta' });
        }
        items.push({ href: '/helpcenterusers', label: 'Help Center', icon: FaInfoCircle });
        return items;
    }

    // STANDARD ORGANIZATION NAVIGATION
    if (isAdmin) {
        items.push({ href: '/subscriptions', label: 'Subscriptions', icon: FaWallet });
        items.push({ href: '/admin', label: 'Org Admin', icon: FaBuilding, requestsBadge: true });

        const orgId = currentOrg.orgId || currentOrg.OrgId;
        // Whole folder is admin-only for standard orgs (legacy NavMenu wraps
        // it in AuthorizeView Roles="orgadmin"); Org App Access routes by
        // org flavor, exactly as the single roster item always did.
        const orgAppAccessHref = isOrgAccountType(currentOrg, 'Private') ? '/namedaccessprivate'
            : orgId === 'lacffassn' ? '/namedaccesslac'
                : '/namedaccess';
        items.push(appAccessFolder(orgAppAccessHref, true));

        items.push({ href: '/portalusers', label: 'Portal Users', icon: FaUsersCog });
        items.push({ href: '/staffing', label: 'Staffing Admin', icon: FaUserShield });
    }

    // Forms Library — legacy NavMenu.razor L215 `AuthorizeView Roles="forms,
    // orgadmin"`: the `forms` role is a claim minted from the org's
    // AppletAdminPermissions (PortalAuthService.cs L266-278; the applet id is
    // "forms", L318), so a Forms applet admin who is not an org admin gets the
    // entry too. It sat inside the org-admin block until §C66 (8 Sep 2026).
    // Same rule as hasAppletRole in utils/builtInApplets.js.
    if (isAdmin || (currentOrg.appletAdminPermissions || []).includes('forms')) {
        items.push({ href: '/formslibrary', label: 'Forms Library', icon: FaWpforms, badge: 'Beta' });
    }

    if (isAdmin && (currentOrg.orgId || currentOrg.OrgId) === 'lacffassn') {
        items.push({ href: '/analytics', label: 'Staff Analytics', icon: FaChartBar });
    }

    // Legacy NavMenu.razor L251-311: the user-ID check wraps each of these
    // four in `<AuthorizeView Roles="orgadmin">` (L260, 273, 286, 301), so a
    // super-admin who is not an admin of the CURRENT org gets none of them
    // (audit 12r).
    if (isSuperAdmin && isAdmin) {
        items.push({ href: '/iconlibrary', label: 'Icon Library', icon: FaImages });
        items.push({ href: '/knownorganizations', label: 'Known Organizations', icon: FaBuilding });
        items.push({ href: '/shiftschedules', label: 'Shift Schedules', icon: FaCalendarAlt });
        items.push({ href: '/fseadmin', label: 'FSE Admin', icon: FaCog });
    }

    // AI Dashboard (owner 2026-09-17): credits, pending AI jobs, delete — "up
    // in the top, above Help Center". Same trial gate as every AI screen
    // (utils/aiAppletsGate: two orgs, the owner and the PM), org admins only.
    if (isAdmin && isAiAppletsEnabled(currentOrg.orgId || currentOrg.OrgId, user?.id)) {
        items.push({ href: '/aidashboard', label: 'AI Dashboard', icon: FaRobot, badge: 'Beta' });
    }

    // Help Center closes out this top section. (Intelligence renders as its
    // own section below the built-in applets — see buildIntelNavItems.)
    items.push({ href: '/helpcenterusers', label: 'Help Center', icon: FaInfoCircle });

    return items;
}

/**
 * Intelligence (Beta) section. Its own list, matching the legacy NavMenu's
 * "Intelligence" group. Gating: an eight-ID user list for the section,
 * org-admin for Admin — distinct from both the UAS gate and the portal
 * super-admin set, see utils/intel.js.
 */
export function buildIntelNavItems({ currentOrg, user, isAdmin }) {
    if (!currentOrg || !isIntelUser(user)) return [];

    // Account types with a reduced menu never reached the legacy Intel
    // block, which sat in the standard-org branch of NavMenu.
    if (isAccountType(currentOrg, 'SharedTrainingCal') ||
        isAccountType(currentOrg, 'SharedHouse') ||
        isOrgAccountType(currentOrg, 'HouseOnly') ||
        isOrgAccountType(currentOrg, 'GroupApplet')) {
        return [];
    }

    const items = [{ href: '/intel/feed', label: 'Intel Feed', icon: FaBroadcastTower }];
    if (isAdmin) {
        items.push({ href: '/intel/admin', label: 'Intel Admin', icon: FaCog });
    }
    return items;
}

/**
 * The nav pages a user can pin as dashboard shortcuts: main nav + intel,
 * minus dividers and minus Home — a Home shortcut ON the home screen would
 * navigate to itself.
 */
export function buildShortcutNavItems(ctx) {
    return [...buildPortalNavItems(ctx), ...buildIntelNavItems(ctx)]
        // A folder isn't pinnable, but every page inside it is.
        .flatMap(i => (i.type === 'folder' ? i.children : [i]))
        .filter(i => !i.type && i.href && i.href !== '/' && i.href !== '/househome');
}
